/**
 * Exclusive upsell.
 *
 * Gates the add behind a configured main-unit purifier in the cart. Adds
 * follow the cart-upsell path (not ProductForm): FormData POST to
 * /cart/add.js, attribution + `_upsell` + `_exclusive_offer`, never `_iom_*`.
 * `_upsell` is shared with the cart drawer carousel (which hides products
 * already taken on it); `_exclusive_offer` is a reporting tag only, so orders
 * can tell this section's adds apart. Nothing in the theme reads it.
 */
(() => {
  const ADDED_STATE_DURATION = 2000;

  if (customElements.get('exclusive-upsell')) return;

  const Base = typeof BaseElement === 'function' ? BaseElement : HTMLElement;

  customElements.define(
    'exclusive-upsell',
    class ExclusiveUpsell extends Base {
      connectedCallback() {
        if (typeof super.connectedCallback === 'function') super.connectedCallback();

        this.on = this.on || ((target, event, handler, options) => {
          target.addEventListener(event, handler, options);
        });

        this.on(this, 'change', (event) => {
          if (event.target.matches('.exclusive-upsell__finish-input')) this.handleFinishChange(event);
        });
        // Capture: hover-button sets aria-busy on form submit. Stop a gated
        // or in-flight click before submit so the loader never starts and a
        // second click cannot POST another /cart/add.
        this.on(this, 'click', (event) => {
          const button = event.target.closest('.exclusive-upsell__add');
          if (!button || !this.form?.contains(button)) return;
          if (this._adding || this._pairing) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          if (this.hasAttribute('data-unlocked')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.rejectAdd(this.gateMessage(), button.getAttribute('data-variant-id'));
          void this.syncGate();
        }, { capture: true });
        this.on(this, 'submit', (event) => {
          if (event.target.matches('.exclusive-upsell__form')) void this.handleAddToCart(event);
        });

        // The featured product form adds the purifier on its own. When the
        // offer checkbox is checked, add the selected shower after that
        // succeeds so IOM sees the purifier before it prices the shower.
        this._onFeaturedAdded = (event) => {
          if (this._pairing || this._adding || !this.offerChecked()) return;
          if (!this.addedMainUnit(event?.detail?.product)) return;
          const task = this.pairShowerAfterPurifier(event);
          // ProductForm awaits `hold` before opening the drawer.
          if (Array.isArray(event.detail?.hold)) event.detail.hold.push(task);
        };
        document.addEventListener('ajaxProduct:added', this._onFeaturedAdded);
        if (typeof this.registerCleanup === 'function') {
          this.registerCleanup(() => {
            document.removeEventListener('ajaxProduct:added', this._onFeaturedAdded);
          });
        }

        if (theme?.pubsub?.subscribe) {
          const unsubscribe = theme.pubsub.subscribe(
            theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.handleCartUpdate.bind(this)
          );
          if (typeof this.registerCleanup === 'function') this.registerCleanup(unsubscribe);
        }

        this.observeIomPrice();
        void this.syncGate();
      }

      get form() {
        return this.querySelector('.exclusive-upsell__form');
      }

      get button() {
        return this.form?.querySelector('button[type="submit"]');
      }

      get label() {
        return this.button?.querySelector('.exclusive-upsell__add-label');
      }

      get mainUnitId() {
        return this.getAttribute('data-main-unit-id') || '';
      }

      get ctaTemplate() {
        return this.getAttribute('data-cta-template') || '';
      }

      get offerPrice() {
        // IOM's painted offer wins; the section's fallback price fills in until it paints.
        return this.paintedOfferPrice() || this.getAttribute('data-fixed-offer-price') || this.getAttribute('data-offer-price') || '';
      }

      /**
       * IOM's painted offer, if the campaign matched. Read-only: never
       * rewrite the leaf. Falls back to Shopify list price otherwise.
       */
      paintedOfferPrice() {
        const discount = this.querySelector('[data-exclusive-upsell-iom-price] .iom-discount-price');
        if (!discount) return '';
        const clone = discount.cloneNode(true);
        clone.querySelectorAll('.iom-discount-amount, .iom-discount-badge').forEach((node) => node.remove());
        return (clone.textContent || '').replace(/\s+/g, ' ').trim();
      }

      observeIomPrice() {
        if (this._iomPriceObserver || typeof MutationObserver !== 'function') return;
        const leaf = this.querySelector('[data-exclusive-upsell-iom-price]');
        if (!leaf) return;
        this._iomPriceObserver = new MutationObserver(() => {
          this.syncButtonState();
        });
        this._iomPriceObserver.observe(leaf, { childList: true, subtree: true, characterData: true });
        if (typeof this.registerCleanup === 'function') {
          this.registerCleanup(() => this._iomPriceObserver?.disconnect());
        }
      }

      cartHasMainUnit(cart) {
        const mainId = this.mainUnitId;
        if (!mainId || !cart?.items) return false;
        return cart.items.some((item) => String(item.product_id) === String(mainId));
      }

      gateMessage() {
        const message = this.getAttribute('data-gate-error');
        if (message) return message;
        const product = this.getAttribute('data-main-unit-title');
        if (product) {
          return `Add ${product} to your cart first to get this shower head at the exclusive price.`;
        }
        return 'Add the featured purifier to your cart first to get this shower head at the exclusive price.';
      }

      addErrorMessage() {
        return this.getAttribute('data-add-error') || "Couldn't add this item. Try again.";
      }

      soldOutMessage() {
        return this.getAttribute('data-sold-out-label')
          || this.button?.getAttribute('data-sold-out-label')
          || this.gateMessage();
      }

      offerChecked() {
        const input = this.querySelector('[data-exclusive-upsell-offer-check]');
        return Boolean(input && input.checked);
      }

      /**
       * /cart/add.js returns one line item, or `{ items: [...] }` when the
       * featured form posted a bundle. The purifier is the main unit.
       */
      addedMainUnit(product) {
        const mainId = this.mainUnitId;
        if (!mainId || !product) return false;
        const lines = Array.isArray(product.items) ? product.items : [product];
        return lines.some((line) => line && String(line.product_id) === String(mainId));
      }

      finishTitle(title) {
        const value = (title || '').trim();
        return !value || value === 'Default Title' ? '' : value;
      }

      /**
       * Fresh-cart checks that must pass before /cart/add. Fail closed.
       * @returns {{ ok: true } | { ok: false, message: string }}
       */
      validateOffer(cart, variantId, available) {
        if (!this.mainUnitId) {
          return { ok: false, message: this.gateMessage() };
        }
        if (!cart || !Array.isArray(cart.items)) {
          return { ok: false, message: this.gateMessage() };
        }
        if (!this.cartHasMainUnit(cart)) {
          return { ok: false, message: this.gateMessage() };
        }
        if (!variantId) {
          return { ok: false, message: this.gateMessage() };
        }
        if (available === false) {
          return { ok: false, message: this.soldOutMessage() };
        }
        return { ok: true };
      }

      clearBusy() {
        const button = this.button;
        if (!button) return;
        button.removeAttribute('aria-busy');
        button.removeAttribute('loading');
      }

      rejectAdd(message, variantId) {
        this.clearBusy();
        this.setUnlocked(false);
        this.showError(message);
        if (theme?.pubsub?.publish) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'exclusive-upsell',
            productVariantId: variantId || '',
            errors: message,
            message,
          });
        }
      }

      async syncGate(cart) {
        let nextCart = cart;
        if (!nextCart) {
          try {
            nextCart = await theme.cartState.fetch();
          }
          catch (error) {
            this.setUnlocked(false);
            return;
          }
        }
        this._lastCart = nextCart;
        this.setUnlocked(this.cartHasMainUnit(nextCart));
        this.applyKnownCaps();
      }

      /**
       * Re-apply per-variant caps from the last cart we already have.
       * Never fetches — a /cart.js failure here would fail-close the gate.
       * @returns {boolean} true when caps were applied from a known cart
       */
      applyKnownCaps() {
        if (!theme.cartLimits || theme.cartLimits.max <= 0 || !this._lastCart) return false;
        theme.cartLimits.applyVariantCaps(this, this._lastCart);
        return true;
      }

      /**
       * End the in-flight / Added hold and restore the CTA from known state:
       * finish availability plus data-max-capped. Do not syncGate().
       * The paired shower POST uses this too, once `_pairing` is already clear.
       */
      restoreAfterAdd() {
        this._adding = false;
        this.syncButtonState();
        this.applyKnownCaps();
      }

      handleCartUpdate(event) {
        const cart = event?.cart?.items ? event.cart : undefined;
        void this.syncGate(cart);
      }

      setUnlocked(unlocked) {
        this.toggleAttribute('data-unlocked', unlocked);
        if (unlocked) this.clearError();
        this.syncButtonState();
      }

      selectedFinish() {
        return this.querySelector('.exclusive-upsell__finish-input:checked')
          || this.querySelector('.exclusive-upsell__finish-input');
      }

      buildCtaLabel(title, available) {
        const button = this.button;
        if (!button) return '';
        if (!available) return button.getAttribute('data-sold-out-label') || '';
        const template = this.ctaTemplate;
        if (!template) return button.getAttribute('data-add-label') || '';
        const finish = this.finishTitle(title);
        const withFinish = finish
          ? template.replaceAll('[finish]', finish)
          : template.replaceAll(' [finish]', '').replaceAll('[finish] ', '').replaceAll('[finish]', '');
        return withFinish.replaceAll('[price]', this.offerPrice).replace(/\s+/g, ' ').trim();
      }

      syncButtonState() {
        const button = this.button;
        // `_adding` covers validation, the POST, the Added cooldown, and the
        // paired shower POST. `_pairing` is the same hold if the flag was
        // claimed first. aria-busy is only the spinner — hover-button sets
        // it on submit, and we clear it before a failed gate so it is not
        // the in-flight signal. Do not re-enable from availability while
        // any of those is set. A purifier cartUpdate unlocks the gate before
        // the paired POST finishes; without this it would enable the CTA.
        if (!button || this._adding || this._pairing || button.getAttribute('aria-busy') === 'true') return;

        const finish = this.selectedFinish();
        const available = finish
          ? finish.getAttribute('data-available') !== 'false'
          : button.getAttribute('data-available') !== 'false';
        const unlocked = this.hasAttribute('data-unlocked');
        const title = finish?.getAttribute('data-title') || button.getAttribute('data-finish-title') || '';

        // Sold-out and per-variant cap stay disabled. The main-unit gate
        // does not: the shopper must be able to click so we can show the
        // validation error. data-max-capped is stamped by applyVariantCaps
        // from a known cart — honour it here so a cooldown restore cannot
        // re-enable a variant that already hit the cap.
        const capped = button.hasAttribute('data-max-capped');
        button.disabled = !available || capped;
        button.setAttribute('aria-disabled', String(!available || capped));
        button.toggleAttribute('data-locked', !unlocked);

        const nextLabel = this.buildCtaLabel(title, available);
        if (available) button.setAttribute('data-add-label', nextLabel);
        if (this.label) {
          const current = this.label.textContent;
          const adding = button.getAttribute('data-adding-label');
          const added = button.getAttribute('data-added-label');
          if (current !== adding && current !== added) this.label.textContent = nextLabel;
        }
      }

      handleFinishChange(event) {
        const input = event.target;
        const form = this.form;
        const idInput = form?.querySelector('input[name="id"]');
        if (idInput) idInput.value = input.value;

        const button = this.button;
        const available = input.getAttribute('data-available') !== 'false';
        this.setAttribute('data-variant-available', String(available));
        if (button) {
          button.setAttribute('data-variant-id', input.value);
          button.setAttribute('data-available', String(available));
          button.setAttribute('data-finish-title', input.getAttribute('data-title') || '');
        }

        const formatted = input.getAttribute('data-price-formatted');
        if (formatted) this.setAttribute('data-offer-price', formatted);

        // Variant id only. IOM owns the leaf contents; do not rewrite them.
        const iomPrice = this.querySelector('[data-exclusive-upsell-iom-price]');
        if (iomPrice) iomPrice.setAttribute('data-variant-id', input.value);

        if (form) {
          if (input.getAttribute('data-price')) form.setAttribute('data-variant-price', input.getAttribute('data-price'));
          if (form.dataset.attributionLineProperties) {
            try {
              const properties = JSON.parse(form.dataset.attributionLineProperties);
              const sku = input.getAttribute('data-sku');
              if (sku) properties._variant_sku = sku;
              else delete properties._variant_sku;
              form.dataset.attributionLineProperties = JSON.stringify(properties);
            }
            catch (error) {
              // Malformed JSON: leave the server-rendered value alone.
            }
          }
        }

        const image = this.querySelector('[data-exclusive-upsell-image]');
        const src = input.getAttribute('data-image');
        if (image && src) {
          image.src = src;
          const srcset = input.getAttribute('data-image-srcset');
          if (srcset) image.srcset = srcset;
          const alt = input.getAttribute('data-image-alt');
          if (alt) image.alt = alt;
        }

        // Re-evaluate the cap for THIS variant. Leaving data-max-capped on
        // the button after a finish change would disable an uncapped finish,
        // and skipping the re-apply would leave a capped finish clickable.
        if (!this.applyKnownCaps() && button) {
          button.removeAttribute('data-max-capped');
          button.removeAttribute('title');
        }
        this.syncButtonState();
      }

      /**
       * Cart from the purifier add, plus this shower at quantity 1.
       * Published so cartUpdate listeners that read `items` (caps, volume
       * pricing) do not see an empty cart and do not start another /cart.js.
       * No line key: nothing may write this row back to the cart.
       */
      carriedCartWithShower(variantId) {
        const previous = this._lastCart;
        if (!previous || !Array.isArray(previous.items) || typeof previous.item_count !== 'number') return null;
        const variant = Number(variantId);
        const productId = this.form?.getAttribute('data-product-id');
        return {
          ...previous,
          item_count: previous.item_count + 1,
          items: previous.items.concat([{
            id: variant,
            variant_id: variant,
            product_id: productId ? Number(productId) : undefined,
            quantity: 1,
            properties: { _upsell: 'true', _exclusive_offer: 'true' },
          }]),
        };
      }

      attachShowerProperties(formData, variantId) {
        formData.set('id', variantId);
        formData.set('quantity', '1');

        const attribution = window.Attribution;
        if (attribution && typeof attribution.buildLineItemProperties === 'function') {
          const properties = attribution.buildLineItemProperties(this.form, variantId) || {};
          if (!properties._upsell) properties._upsell = 'true';
          properties._exclusive_offer = 'true';
          for (const [key, value] of Object.entries(properties)) {
            if (key.startsWith('_iom_')) continue;
            formData.set(`properties[${key}]`, String(value));
          }
        }
        else {
          formData.set('properties[_upsell]', 'true');
          formData.set('properties[_exclusive_offer]', 'true');
        }
      }

      /**
       * POST the selected shower. The purifier must already be in the cart
       * so IOM can price this line. Does not set a selling plan.
       * `refreshCart: false` publishes the drawer HTML from this response
       * without another /cart.js. The offer-box CTA keeps the read. The
       * paired add does not: that read was only so the count and the
       * remembered cart could travel with markup the POST already returned.
       * `trustCarried: false` means that remembered cart is from before the
       * purifier add (its /cart.js read threw). Skip it and read again.
       *
       * Takes the cart engine's turn, so the POST cannot overlap a drawer
       * quantity write and the drawer re-render from it lands before the next
       * write. cart.js is not loaded on every page (cart page mode) and runs
       * after this file, so it is looked up here and the add runs unlocked
       * without it: with no engine there is no rendered cart to protect.
       * @returns {Promise<{ ok: true, parsedState: object } | { ok: false, message: string }>}
       */
      async sendShower(formData, variantId, { refreshCart = true, focus = null, trustCarried = true } = {}) {
        const engine = theme?.cartEngine;
        const exclusive = engine && typeof engine.runExclusive === 'function'
          ? (task) => engine.runExclusive(task)
          : (task) => task({ fetch: (url, options) => fetch(url, options) });

        let outcome;
        try {
          outcome = await exclusive((io) => this.postShower(io, formData, variantId, { refreshCart, trustCarried }));
        }
        catch (error) {
          // No answer (network failure, or the engine's 15s ceiling): the add
          // may have landed, so the drawer re-renders from server truth. The
          // callers show the message; it sits outside the drawer.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          throw error;
        }

        // ProductForm opens the drawer after the featured add. This path is
        // the offer CTA, so the drawer opens here, after the section HTML is
        // in the cart. The paired add leaves it to ProductForm, which is
        // still waiting on this request. A failed cart read already opened
        // via cart:refresh. Outside the engine's turn: opening the drawer is
        // not a cart write.
        if (outcome.openDrawer) this.openCartDrawer(focus);
        return outcome.result;
      }

      /** The write itself, run inside sendShower's turn. */
      async postShower(io, formData, variantId, { refreshCart, trustCarried }) {
        // Gathered inside the turn, so it describes the cart at write time.
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
        );
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);

        const config = theme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await io.fetch(theme.routes.cart_add_url, config);
        const text = await response.text();
        let parsedState;
        try {
          parsedState = JSON.parse(text);
        }
        catch (error) {
          return { result: { ok: false, message: this.addErrorMessage() } };
        }

        if (!response.ok || parsedState.status) {
          const message = parsedState.description || parsedState.message || this.addErrorMessage();
          if (theme?.pubsub?.publish) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'exclusive-upsell',
              productVariantId: variantId,
              errors: parsedState.errors || parsedState.description || message,
              message: parsedState.message || message,
            });
          }
          return { result: { ok: false, message } };
        }

        const carried = trustCarried ? this.carriedCartWithShower(variantId) : null;
        if (!refreshCart && parsedState.sections && carried) {
          carried.sections = parsedState.sections;
          this._lastCart = carried;
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'exclusive-upsell',
            productVariantId: variantId,
            cart: carried,
          });
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
          return { result: { ok: true, parsedState } };
        }

        let nextCart = null;
        try {
          nextCart = { ...(await theme.cartState.fetch({ fresh: true })) };
        }
        catch (cartError) {
          console.error(cartError);
        }

        if (nextCart) {
          this._lastCart = nextCart;
          nextCart.sections = parsedState.sections;
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'exclusive-upsell',
            productVariantId: variantId,
            cart: nextCart,
          });
        }
        else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        }
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
        return { result: { ok: true, parsedState }, openDrawer: Boolean(refreshCart && nextCart) };
      }

      /**
       * Offer-box success. A cart page has no drawer to open. Focus returns
       * to the CTA when the drawer closes, same as ProductForm.
       */
      openCartDrawer(focus) {
        if (document.body?.classList?.contains('template-cart')) return;
        if (theme.settings?.cartType === 'page') return;
        const drawer = document.querySelector?.('cart-drawer');
        if (drawer && typeof drawer.show === 'function') drawer.show(focus || null);
      }

      /**
       * Featured-product add succeeded and the offer checkbox is checked.
       * Add the shower the shopper selected in this box. Quantity is 1.
       */
      async pairShowerAfterPurifier(event) {
        if (this._pairing || this._adding || !this.offerChecked()) return;
        if (!this.addedMainUnit(event?.detail?.product)) return;

        const form = this.form;
        if (!form) return;
        const finish = this.selectedFinish();
        const variantId = String(finish?.value || form.querySelector('[name="id"]')?.value || '');
        const available = finish
          ? finish.getAttribute('data-available') !== 'false'
          : this.getAttribute('data-variant-available') !== 'false';
        if (!variantId) {
          this.showError(this.gateMessage());
          return;
        }
        if (available === false) {
          this.showError(this.soldOutMessage());
          return;
        }

        // The featured form publishes the post-add cart before this event, and
        // the shower quantity is always 1, so another read here only delays
        // the POST. `cartRead: false` means that read threw: `_lastCart` is
        // still the pre-add cart. The shower quantity on it is unchanged, so
        // the cap can use it, but it must not be published as the new cart.
        const cart = this._lastCart;
        const trustCarried = event?.detail?.cartRead !== false;
        const cap = theme.cartLimits ? theme.cartLimits.max : 0;
        if (cart && cap > 0 && theme.cartLimits.variantQuantityInCart(cart, variantId) + 1 > cap) {
          theme.cartLimits.applyVariantCaps(this, cart);
          const capMessage = theme.cartLimits.message();
          if (capMessage) {
            this.showError(capMessage);
            if (theme?.pubsub?.publish) {
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
                source: 'exclusive-upsell',
                productVariantId: variantId,
                errors: capMessage,
                message: capMessage,
              });
            }
          }
          return;
        }

        // Claim the same hold as the offer CTA before the POST. The purifier
        // add already published cartUpdate, which unlocks this button, and
        // the click / submit paths only refuse `_adding`. `_pairing` alone
        // left a second /cart/add open for the whole shower request.
        this._pairing = true;
        this._adding = true;
        const button = this.button;
        if (button) button.disabled = true;
        try {
          const formData = new FormData(form);
          this.attachShowerProperties(formData, variantId);
          const result = await this.sendShower(formData, variantId, { refreshCart: false, trustCarried });
          if (!result.ok) this.showError(result.message);
        }
        catch (error) {
          console.error(error);
          this.showError(this.addErrorMessage());
        }
        finally {
          this._pairing = false;
          this.restoreAfterAdd();
        }
      }

      async handleAddToCart(event) {
        event.preventDefault();
        event.stopPropagation();

        const form = event.target;
        const button = form.querySelector('button[type="submit"]');
        // Do not treat hover-button's submit-time aria-busy as an in-flight add.
        if (!button) return;
        if (this._adding || this._pairing) {
          this.clearBusy();
          return;
        }

        // Claim the add before any await. The cart read and cap checks are
        // async; leaving `_adding` false until they finish lets a second
        // click POST another /cart/add.
        this._adding = true;
        button.disabled = true;

        const available = button.getAttribute('data-available') !== 'false';
        const formData = new FormData(form);
        const variantId = String(formData.get('id') || button.getAttribute('data-variant-id') || '');

        // hover-button already flipped aria-busy on submit. Drop it until
        // validation passes so a gate failure never looks like a spinner.
        this.clearBusy();

        if (!this.hasAttribute('data-unlocked')) {
          this._adding = false;
          this.rejectAdd(this.gateMessage(), variantId);
          void this.syncGate();
          return;
        }

        let cart = null;
        try {
          cart = await theme.cartState.fetch({ fresh: true });
        }
        catch (error) {
          this._adding = false;
          this.rejectAdd(this.gateMessage(), variantId);
          return;
        }

        const gate = this.validateOffer(cart, variantId, available);
        if (!gate.ok) {
          this._adding = false;
          this.rejectAdd(gate.message, variantId);
          this.clearBusy();
          return;
        }

        const cap = theme.cartLimits ? theme.cartLimits.max : 0;
        if (cap > 0 && theme.cartLimits.variantQuantityInCart(cart, variantId) + 1 > cap) {
          this._adding = false;
          theme.cartLimits.applyVariantCaps(this, cart);
          const capMessage = theme.cartLimits.message();
          if (capMessage) {
            this.clearBusy();
            this.showError(capMessage);
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'exclusive-upsell',
              productVariantId: variantId,
              errors: capMessage,
              message: capMessage,
            });
          }
          else {
            this.clearBusy();
          }
          return;
        }

        const label = this.label;
        const originalLabel = label?.textContent;
        button.setAttribute('aria-busy', 'true');
        if (label && button.getAttribute('data-adding-label')) {
          label.textContent = button.getAttribute('data-adding-label');
        }

        this.attachShowerProperties(formData, variantId);

        let holdForAdded = false;
        try {
          const result = await this.sendShower(formData, variantId, { focus: button });
          if (!result.ok) {
            this.showError(result.message);
            return;
          }

          if (button.isConnected && label && button.getAttribute('data-added-label')) {
            holdForAdded = true;
            label.textContent = button.getAttribute('data-added-label');
            setTimeout(() => {
              if (!button.isConnected) {
                this._adding = false;
                return;
              }
              if (label.textContent === button.getAttribute('data-sold-out-label')) {
                this._adding = false;
                return;
              }
              label.textContent = originalLabel;
              this.restoreAfterAdd();
            }, ADDED_STATE_DURATION);
          }
        }
        catch (error) {
          console.error(error);
          if (label) label.textContent = originalLabel;
          this.showError(this.addErrorMessage());
        }
        finally {
          if (button.isConnected) {
            this.clearBusy();
            if (label && (!label.textContent || label.textContent === button.getAttribute('data-adding-label'))) {
              label.textContent = originalLabel;
            }
          }
          // Keep `_adding` and the disabled CTA through the Added cooldown
          // so a second click cannot POST again. `syncButtonState` would
          // otherwise turn the button back on from availability alone.
          // Re-assert disabled: a cartUpdate during the POST can run
          // applyVariantCaps and enable the button before we get here.
          if (holdForAdded) {
            if (button.isConnected) button.disabled = true;
          }
          else {
            if (button.isConnected) this.restoreAfterAdd();
            else this._adding = false;
          }
        }
      }

      showError(message) {
        const error = this.querySelector('[data-exclusive-upsell-error]');
        if (!error || !message) return;
        error.textContent = message;
        error.hidden = false;
        error.focus?.();
        error.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      clearError() {
        const error = this.querySelector('[data-exclusive-upsell-error]');
        if (!error) return;
        error.textContent = '';
        error.hidden = true;
      }
    }
  );
})();
